export type GameStatus = 'idle' | 'playing' | 'paused' | 'ended';

export interface GameState {
  status: GameStatus;
  score: number;
  timeRemaining: number;
  missedCabinets: number;
  hitCabinets: number;
  level: number;
  autoAimActive: boolean;
  autoAimRemaining: number;
}

export type TargetCategory = 'good' | 'bad' | 'special';
export type TargetAnimation = 'pop-up' | 'fly-by';

export interface TargetConfig {
  id: string;
  type: string;
  size: 'l' | 'm' | 's';
  points: number;
  penalty: number;
  animation: TargetAnimation;
  category: TargetCategory;
  image: string;
  hitImage?: string;
}

export interface Target {
  id: string;
  config: TargetConfig;
  x: number;
  y: number;
  width: number;
  height: number;
  isHit: boolean;
  animationDuration: number;
  createdAt: number;
}

export interface GameConfig {
  gameTimeMs: number;
  spawnIntervalStartMs: number;
  spawnIntervalMinMs: number;
  maxMissedCabinets: number;
  autoAimDurationMs: number;
  machineArea: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

export const GAME_CONFIG: GameConfig = {
  gameTimeMs: 30000,
  spawnIntervalStartMs: 1200,
  spawnIntervalMinMs: 300,
  maxMissedCabinets: 10,
  autoAimDurationMs: 5000,
  machineArea: {
    minX: 0.15,
    maxX: 0.95,
    minY: 0.30,
    maxY: 0.80,
  },
};

export const BASE_SIZES = {
  cabinet: { l: { w: 100, h: 125 }, m: { w: 80, h: 100 }, s: { w: 60, h: 75 } },
  vario: { l: { w: 120, h: 72 }, m: { w: 100, h: 60 }, s: { w: 80, h: 48 } },
  logo: { m: { w: 120, h: 72 } },
};

export const TARGET_CONFIGS: Record<string, TargetConfig> = {
  cabinet_l: {
    id: 'cabinet_l',
    type: 'cabinet1',
    size: 'l',
    points: 50,
    penalty: 0,
    animation: 'pop-up',
    category: 'good',
    image: '/assets/cabinet-transformer/schaltschrank1.png',
    hitImage: '/assets/cabinet-transformer/varioX.png',
  },
  cabinet_m: {
    id: 'cabinet_m',
    type: 'cabinet2',
    size: 'm',
    points: 100,
    penalty: 0,
    animation: 'pop-up',
    category: 'good',
    image: '/assets/cabinet-transformer/schaltschrank2.jpg',
    hitImage: '/assets/cabinet-transformer/impact.png',
  },
  cabinet_s: {
    id: 'cabinet_s',
    type: 'cabinet3',
    size: 's',
    points: 200,
    penalty: 0,
    animation: 'pop-up',
    category: 'good',
    image: '/assets/cabinet-transformer/schaltschrank3.png',
    hitImage: '/assets/cabinet-transformer/varioXmini.png',
  },
  vario_l: {
    id: 'vario_l',
    type: 'vario1',
    size: 'l',
    points: 0,
    penalty: 100,
    animation: 'pop-up',
    category: 'bad',
    image: '/assets/cabinet-transformer/impact.png',
  },
  vario_m: {
    id: 'vario_m',
    type: 'vario2',
    size: 'm',
    points: 0,
    penalty: 150,
    animation: 'pop-up',
    category: 'bad',
    image: '/assets/cabinet-transformer/emparro.png',
  },
  vario_s: {
    id: 'vario_s',
    type: 'vario3',
    size: 's',
    points: 0,
    penalty: 250,
    animation: 'pop-up',
    category: 'bad',
    image: '/assets/cabinet-transformer/xelity67.png',
  },
  logo: {
    id: 'logo',
    type: 'logo',
    size: 'm',
    points: 500,
    penalty: 0,
    animation: 'fly-by',
    category: 'special',
    image: '/assets/cabinet-transformer/logo.svg',
  },
};

export const SPAWN_CHANCES = [
  'cabinet_l', 'cabinet_l',
  'cabinet_m', 'cabinet_m', 'cabinet_m',
  'cabinet_s', 'cabinet_s',
  'vario_l', 'vario_l',
  'vario_m',
  'vario_s',
];
